import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export interface SkyspaceSettings {
  reduceMotion: boolean;
  highContrast: boolean;
  largeText: boolean;
  disableCustomEffects: boolean;
  showSensitiveContent: boolean;
}

interface SettingsState {
  settings: SkyspaceSettings;
  update(patch: Partial<SkyspaceSettings>): void;
  reset(): void;
}

const STORAGE_KEY = 'skyspace-settings-v1';
const defaults: SkyspaceSettings = {
  reduceMotion: false,
  highContrast: false,
  largeText: false,
  disableCustomEffects: false,
  showSensitiveContent: false,
};

function initialSettings(): SkyspaceSettings {
  if (typeof window === 'undefined') return defaults;
  let saved: Partial<SkyspaceSettings> = {};
  try {
    saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') as Partial<SkyspaceSettings>;
  } catch {
    saved = {};
  }
  return {
    ...defaults,
    reduceMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    ...saved,
  };
}

const SettingsContext = createContext<SettingsState | undefined>(undefined);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState(initialSettings);
  useEffect(() => {
    const receivePrivateSettings = (event: Event) => {
      const detail = (event as CustomEvent<{
        accountDid?: string;
        state?: { settings?: Partial<SkyspaceSettings> };
      }>).detail;
      if (!detail?.accountDid || !detail.state?.settings) return;
      setSettings(current => ({ ...current, ...detail.state!.settings }));
    };
    window.addEventListener('skyspace-private-state-updated', receivePrivateSettings);
    return () => {
      window.removeEventListener('skyspace-private-state-updated', receivePrivateSettings);
    };
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    const root = document.documentElement;
    root.classList.toggle('skyspace-reduce-motion', settings.reduceMotion);
    root.classList.toggle('skyspace-high-contrast', settings.highContrast);
    root.classList.toggle('skyspace-large-text', settings.largeText);
    root.classList.toggle('skyspace-disable-effects', settings.disableCustomEffects);
    if (
      settings.reduceMotion
      || settings.highContrast
      || settings.largeText
      || settings.disableCustomEffects
    ) {
      void import('./settings-effects.css');
    }
  }, [settings]);
  const value = useMemo<SettingsState>(() => ({
    settings,
    update: patch => {
      setSettings(current => ({ ...current, ...patch }));
      void import('../private-state/runtime').then(({ updatePrivateSettings }) => {
        updatePrivateSettings(patch);
      });
    },
    reset: () => {
      setSettings({ ...defaults });
      void import('../private-state/runtime').then(({ updatePrivateSettings }) => {
        updatePrivateSettings(defaults);
      });
    },
  }), [settings]);
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsState {
  const value = useContext(SettingsContext);
  if (!value) throw new Error('useSettings must be used inside SettingsProvider.');
  return value;
}
